/**
 * View Generator Utilities
 *
 * Generate different visualizations from existing ArchiMate diagrams:
 * - Layered view (by ArchiMate layer)
 * - Dependency graph
 * - Data flow view
 * - Component hierarchy
 */

import type {
  ArchiMateDiagram,
  ArchiMateElement,
  ArchiMateRelationship,
  Layer,
  RelationshipType,
} from './types';
import type { ExtendedArchiMateDiagram } from './extended-types';

// ============================================================================
// VIEW TYPES
// ============================================================================

export interface LayeredViewData {
  type: 'layered';
  layers: Array<{
    layer: Layer;
    elements: ArchiMateElement[];
    elementCount: number;
  }>;
  relationships: ArchiMateRelationship[];
  crossLayerConnections: number;
}

export interface DependencyNode {
  element: ArchiMateElement;
  dependencies: string[]; // Element IDs this depends on
  dependents: string[]; // Element IDs that depend on this
  level: number; // Depth in hierarchy (0 = root)
}

export interface DependencyGraphData {
  type: 'dependency';
  nodes: DependencyNode[];
  roots: string[]; // Element IDs with no dependencies
  leaves: string[]; // Element IDs with no dependents
  maxDepth: number;
  cycles: string[][]; // Circular dependencies
}

export interface DataFlowEdge {
  from: string;
  to: string;
  relationship: ArchiMateRelationship;
  flowType: 'access' | 'flow' | 'triggering';
  dataObjects: string[]; // Data objects involved
}

export interface DataFlowViewData {
  type: 'dataflow';
  nodes: ArchiMateElement[];
  edges: DataFlowEdge[];
  dataObjects: ArchiMateElement[];
  sources: string[]; // Element IDs that produce data
  sinks: string[]; // Element IDs that consume data
}

export interface HierarchyNode {
  element: ArchiMateElement;
  children: string[]; // Element IDs
  parent?: string;
  level: number;
}

export interface ComponentHierarchyData {
  type: 'hierarchy';
  nodes: HierarchyNode[];
  roots: string[]; // Top-level elements
  maxDepth: number;
}

export type ViewData =
  | LayeredViewData
  | DependencyGraphData
  | DataFlowViewData
  | ComponentHierarchyData;

// ============================================================================
// LAYERED VIEW
// ============================================================================

/**
 * Generate a layered view grouping elements by ArchiMate layer
 */
export function generateLayeredView(diagram: ArchiMateDiagram): LayeredViewData {
  // Group elements by layer
  const layerMap = new Map<Layer, ArchiMateElement[]>();

  for (const element of diagram.elements) {
    if (!layerMap.has(element.layer)) {
      layerMap.set(element.layer, []);
    }
    layerMap.get(element.layer)!.push(element);
  }

  // Convert to sorted array (strategy -> business -> application -> technology -> physical)
  const layerOrder: Layer[] = [
    'strategy',
    'business',
    'application',
    'technology',
    'physical',
    'implementation',
    'motivation',
  ];

  const layers = layerOrder
    .filter(layer => layerMap.has(layer))
    .map(layer => ({
      layer,
      elements: layerMap.get(layer)!,
      elementCount: layerMap.get(layer)!.length,
    }));

  // Count cross-layer connections
  let crossLayerConnections = 0;
  for (const rel of diagram.relationships) {
    const source = diagram.elements.find(e => e.id === rel.sourceId);
    const target = diagram.elements.find(e => e.id === rel.targetId);
    if (source && target && source.layer !== target.layer) {
      crossLayerConnections++;
    }
  }

  return {
    type: 'layered',
    layers,
    relationships: diagram.relationships,
    crossLayerConnections,
  };
}

// ============================================================================
// DEPENDENCY GRAPH
// ============================================================================

/**
 * Generate a dependency graph based on serving/realization relationships
 */
export function generateDependencyGraph(diagram: ArchiMateDiagram): DependencyGraphData {
  const elementIds = new Set(diagram.elements.map(e => e.id));

  // Build dependency map
  const dependencyMap = new Map<string, { dependencies: Set<string>; dependents: Set<string> }>();

  for (const element of diagram.elements) {
    dependencyMap.set(element.id, { dependencies: new Set(), dependents: new Set() });
  }

  // Analyze relationships that indicate dependencies
  const dependencyRelTypes: RelationshipType[] = [
    'serving',
    'realization',
    'assignment',
    'triggering',
    'flow',
    'access',
  ];

  for (const rel of diagram.relationships) {
    if (dependencyRelTypes.includes(rel.type)) {
      // Target depends on source
      const sourceData = dependencyMap.get(rel.sourceId);
      const targetData = dependencyMap.get(rel.targetId);

      if (sourceData && targetData) {
        targetData.dependencies.add(rel.sourceId);
        sourceData.dependents.add(rel.targetId);
      }
    }
  }

  // Find roots (no dependencies)
  const roots: string[] = [];
  for (const [id, { dependencies }] of dependencyMap) {
    if (dependencies.size === 0) {
      roots.push(id);
    }
  }

  // Find leaves (no dependents)
  const leaves: string[] = [];
  for (const [id, { dependents }] of dependencyMap) {
    if (dependents.size === 0) {
      leaves.push(id);
    }
  }

  // Calculate levels (depth from root)
  const levels = new Map<string, number>();
  const visited = new Set<string>();

  function calculateLevel(elementId: string, currentLevel: number, path: Set<string>): number {
    if (path.has(elementId)) {
      // Cycle detected
      return currentLevel;
    }

    if (levels.has(elementId)) {
      return levels.get(elementId)!;
    }

    const deps = dependencyMap.get(elementId)?.dependencies || new Set();
    if (deps.size === 0) {
      levels.set(elementId, 0);
      return 0;
    }

    const newPath = new Set(path);
    newPath.add(elementId);

    let maxDepLevel = 0;
    for (const depId of deps) {
      const depLevel = calculateLevel(depId, currentLevel + 1, newPath);
      maxDepLevel = Math.max(maxDepLevel, depLevel);
    }

    const level = maxDepLevel + 1;
    levels.set(elementId, level);
    return level;
  }

  for (const elementId of elementIds) {
    calculateLevel(elementId, 0, new Set());
  }

  // Detect cycles
  const cycles: string[][] = [];
  const cycleDetectionVisited = new Set<string>();
  const recursionStack = new Set<string>();

  function detectCycles(elementId: string, path: string[]): void {
    if (recursionStack.has(elementId)) {
      // Found cycle
      const cycleStart = path.indexOf(elementId);
      if (cycleStart !== -1) {
        cycles.push(path.slice(cycleStart));
      }
      return;
    }

    if (cycleDetectionVisited.has(elementId)) {
      return;
    }

    cycleDetectionVisited.add(elementId);
    recursionStack.add(elementId);
    path.push(elementId);

    const deps = dependencyMap.get(elementId)?.dependencies || new Set();
    for (const depId of deps) {
      detectCycles(depId, [...path]);
    }

    recursionStack.delete(elementId);
  }

  for (const elementId of elementIds) {
    detectCycles(elementId, []);
  }

  // Build nodes
  const nodes: DependencyNode[] = diagram.elements.map(element => ({
    element,
    dependencies: Array.from(dependencyMap.get(element.id)?.dependencies || []),
    dependents: Array.from(dependencyMap.get(element.id)?.dependents || []),
    level: levels.get(element.id) || 0,
  }));

  const maxDepth = Math.max(...Array.from(levels.values()), 0);

  return {
    type: 'dependency',
    nodes,
    roots,
    leaves,
    maxDepth,
    cycles,
  };
}

// ============================================================================
// DATA FLOW VIEW
// ============================================================================

/**
 * Generate a data flow view showing access and flow relationships
 */
export function generateDataFlowView(diagram: ArchiMateDiagram): DataFlowViewData {
  const dataFlowRelTypes: RelationshipType[] = ['access', 'flow', 'triggering'];

  // Find data objects
  const dataObjectTypes = ['data-object', 'business-object', 'artifact'];
  const dataObjects = diagram.elements.filter(e =>
    dataObjectTypes.includes(e.type)
  );

  // Build edges
  const edges: DataFlowEdge[] = [];
  const nodeIds = new Set<string>();

  for (const rel of diagram.relationships) {
    if (dataFlowRelTypes.includes(rel.type)) {
      const flowType = rel.type as 'access' | 'flow' | 'triggering';

      // Find related data objects
      const relatedDataObjects: string[] = [];

      // Check if source or target is a data object
      const source = diagram.elements.find(e => e.id === rel.sourceId);
      const target = diagram.elements.find(e => e.id === rel.targetId);

      if (source && dataObjectTypes.includes(source.type)) {
        relatedDataObjects.push(source.id);
      }
      if (target && dataObjectTypes.includes(target.type)) {
        relatedDataObjects.push(target.id);
      }

      edges.push({
        from: rel.sourceId,
        to: rel.targetId,
        relationship: rel,
        flowType,
        dataObjects: relatedDataObjects,
      });

      nodeIds.add(rel.sourceId);
      nodeIds.add(rel.targetId);
    }
  }

  // Get nodes involved in data flow
  const nodes = diagram.elements.filter(e => nodeIds.has(e.id));

  // Find sources (elements that only produce data)
  const sources: string[] = [];
  const sinks: string[] = [];

  for (const nodeId of nodeIds) {
    const outgoing = edges.filter(e => e.from === nodeId);
    const incoming = edges.filter(e => e.to === nodeId);

    if (outgoing.length > 0 && incoming.length === 0) {
      sources.push(nodeId);
    }
    if (incoming.length > 0 && outgoing.length === 0) {
      sinks.push(nodeId);
    }
  }

  return {
    type: 'dataflow',
    nodes,
    edges,
    dataObjects,
    sources,
    sinks,
  };
}

// ============================================================================
// COMPONENT HIERARCHY
// ============================================================================

/**
 * Generate a component hierarchy based on composition/aggregation relationships
 */
export function generateComponentHierarchy(diagram: ArchiMateDiagram): ComponentHierarchyData {
  const hierarchyRelTypes: RelationshipType[] = ['composition', 'aggregation'];

  // Build parent-child relationships
  const childToParent = new Map<string, string>();
  const parentToChildren = new Map<string, Set<string>>();

  for (const rel of diagram.relationships) {
    if (hierarchyRelTypes.includes(rel.type)) {
      // In composition/aggregation, source contains target
      childToParent.set(rel.targetId, rel.sourceId);

      if (!parentToChildren.has(rel.sourceId)) {
        parentToChildren.set(rel.sourceId, new Set());
      }
      parentToChildren.get(rel.sourceId)!.add(rel.targetId);
    }
  }

  // Find roots (elements with no parent)
  const roots: string[] = [];
  for (const element of diagram.elements) {
    if (!childToParent.has(element.id)) {
      roots.push(element.id);
    }
  }

  // Calculate levels
  const levels = new Map<string, number>();

  function calculateLevel(elementId: string): number {
    if (levels.has(elementId)) {
      return levels.get(elementId)!;
    }

    const parentId = childToParent.get(elementId);
    if (!parentId) {
      levels.set(elementId, 0);
      return 0;
    }

    const parentLevel = calculateLevel(parentId);
    const level = parentLevel + 1;
    levels.set(elementId, level);
    return level;
  }

  for (const element of diagram.elements) {
    calculateLevel(element.id);
  }

  // Build nodes
  const nodes: HierarchyNode[] = diagram.elements.map(element => ({
    element,
    children: Array.from(parentToChildren.get(element.id) || []),
    parent: childToParent.get(element.id),
    level: levels.get(element.id) || 0,
  }));

  const maxDepth = Math.max(...Array.from(levels.values()), 0);

  return {
    type: 'hierarchy',
    nodes,
    roots,
    maxDepth,
  };
}

// ============================================================================
// VIEW STATISTICS
// ============================================================================

export interface ViewStatistics {
  elementCount: number;
  relationshipCount: number;
  layerDistribution: Record<Layer, number>;
  typeDistribution: Record<string, number>;
  complexity: number; // Calculated metric
  depth: number;
}

export function calculateViewStatistics(
  diagram: ArchiMateDiagram,
  viewData: ViewData
): ViewStatistics {
  // Count elements by layer
  const layerDistribution: Record<string, number> = {};
  for (const element of diagram.elements) {
    layerDistribution[element.layer] = (layerDistribution[element.layer] || 0) + 1;
  }

  // Count elements by type
  const typeDistribution: Record<string, number> = {};
  for (const element of diagram.elements) {
    typeDistribution[element.type] = (typeDistribution[element.type] || 0) + 1;
  }

  // Calculate complexity (relationships per element)
  const complexity = diagram.elements.length > 0
    ? diagram.relationships.length / diagram.elements.length
    : 0;

  // Get depth from view data
  let depth = 0;
  if (viewData.type === 'dependency') {
    depth = viewData.maxDepth;
  } else if (viewData.type === 'hierarchy') {
    depth = viewData.maxDepth;
  } else if (viewData.type === 'layered') {
    depth = viewData.layers.length;
  }

  return {
    elementCount: diagram.elements.length,
    relationshipCount: diagram.relationships.length,
    layerDistribution: layerDistribution as Record<Layer, number>,
    typeDistribution,
    complexity: Math.round(complexity * 100) / 100,
    depth,
  };
}

// ============================================================================
// VIEW EXPORT
// ============================================================================

export interface ViewExportOptions {
  includeStatistics: boolean;
  includeMetadata: boolean;
  format: 'json' | 'markdown' | 'svg' | 'png';
}

export function exportView(
  diagram: ExtendedArchiMateDiagram,
  viewData: ViewData,
  options: ViewExportOptions = { includeStatistics: true, includeMetadata: false, format: 'json' }
): string {
  if (options.format === 'json') {
    const output: any = {
      view: viewData,
    };

    if (options.includeStatistics) {
      output.statistics = calculateViewStatistics(diagram, viewData);
    }

    if (options.includeMetadata) {
      output.metadata = {
        diagramId: diagram.id,
        diagramName: diagram.name,
        generatedAt: new Date().toISOString(),
      };
    }

    return JSON.stringify(output, null, 2);
  }

  if (options.format === 'markdown') {
    return generateViewMarkdown(diagram, viewData, options);
  }

  throw new Error(`Export format ${options.format} not yet implemented`);
}

function generateViewMarkdown(
  diagram: ExtendedArchiMateDiagram,
  viewData: ViewData,
  options: ViewExportOptions
): string {
  let md = `# ${diagram.name} - ${viewData.type.charAt(0).toUpperCase() + viewData.type.slice(1)} View\n\n`;

  if (options.includeMetadata) {
    md += `**Generated**: ${new Date().toISOString()}\n\n`;
  }

  if (viewData.type === 'layered') {
    md += `## Layers\n\n`;
    for (const layer of viewData.layers) {
      md += `### ${layer.layer.charAt(0).toUpperCase() + layer.layer.slice(1)} (${layer.elementCount} elements)\n\n`;
      for (const element of layer.elements) {
        md += `- **${element.name}** (${element.type})\n`;
      }
      md += '\n';
    }
    md += `**Cross-layer connections**: ${viewData.crossLayerConnections}\n\n`;
  }

  if (viewData.type === 'dependency') {
    md += `## Dependency Graph\n\n`;
    md += `- **Max depth**: ${viewData.maxDepth}\n`;
    md += `- **Root elements**: ${viewData.roots.length}\n`;
    md += `- **Leaf elements**: ${viewData.leaves.length}\n`;
    md += `- **Cycles detected**: ${viewData.cycles.length}\n\n`;

    if (viewData.cycles.length > 0) {
      md += `### Circular Dependencies\n\n`;
      for (const cycle of viewData.cycles) {
        const names = cycle.map(id => {
          const elem = diagram.elements.find(e => e.id === id);
          return elem?.name || id;
        });
        md += `- ${names.join(' → ')}\n`;
      }
      md += '\n';
    }
  }

  if (viewData.type === 'dataflow') {
    md += `## Data Flow\n\n`;
    md += `- **Nodes**: ${viewData.nodes.length}\n`;
    md += `- **Edges**: ${viewData.edges.length}\n`;
    md += `- **Data objects**: ${viewData.dataObjects.length}\n`;
    md += `- **Sources**: ${viewData.sources.length}\n`;
    md += `- **Sinks**: ${viewData.sinks.length}\n\n`;
  }

  if (viewData.type === 'hierarchy') {
    md += `## Component Hierarchy\n\n`;
    md += `- **Max depth**: ${viewData.maxDepth}\n`;
    md += `- **Root elements**: ${viewData.roots.length}\n\n`;
  }

  if (options.includeStatistics) {
    const stats = calculateViewStatistics(diagram, viewData);
    md += `## Statistics\n\n`;
    md += `- **Total elements**: ${stats.elementCount}\n`;
    md += `- **Total relationships**: ${stats.relationshipCount}\n`;
    md += `- **Complexity**: ${stats.complexity}\n`;
    md += `- **Depth**: ${stats.depth}\n\n`;
  }

  return md;
}
